import { ArrowForward, Close, TrendingUp } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, LinearProgress, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import SpaceGradientButton from '../../_UnderConstruction/Onboarding/components/SpaceGradientButton';

interface OnboardingWelcomeDialogProps {
    id?: string;
    title?: string;
    firstName: string;
    isCompleted: boolean;
    progress?: number;
    currentStep?: number;
    totalSteps?: number;
    onStart: () => void;
    onContinue: () => void;
    onClose: () => void;
    resolve?: (data?: unknown) => void;
    dialogStateProps?: {
        title: string;
        key: string;
        id: string;
        windowState: 'normal' | 'minimized' | 'full';
    };
}

export default function OnboardingWelcomeDialog({
    id,
    firstName,
    isCompleted,
    progress = 0,
    currentStep = 0,
    totalSteps = 9,
    onStart,
    onContinue,
    onClose,
    resolve,
    dialogStateProps,
}: OnboardingWelcomeDialogProps) {
    const theme = useTheme();
    const dialogStates = useSelector((state: AppState) => state.dialogs.dialogs);
    const dialogState = dialogStates.find((d) => d.id === (id || dialogStateProps?.id));

    const isInProgress = progress > 0 && !isCompleted;
    const progressPercent = Math.round(progress);
    const isMinimized = dialogState?.windowState === 'minimized';

    const handleClose = () => {
        if (resolve) {
            resolve();
        } else {
            onClose();
        }
    };

    const handleStart = () => {
        onStart();
        handleClose();
    };

    const handleContinue = () => {
        onContinue();
        handleClose();
    };

    if (isMinimized) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#000', 0.7),
                backdropFilter: 'blur(8px)',
                zIndex: dialogState?.zIndex || 2000,
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    maxWidth: 480,
                    mx: 2,
                    p: 4,
                    borderRadius: 3,
                    background: isInProgress
                        ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.95)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.secondary.main, 0.95)} 100%)`,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha('#fff', 0.2)}`,
                    boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.4)}`,
                    color: 'white',
                    textAlign: 'center',
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        color: 'white',
                        zIndex: 1,
                        pointerEvents: 'auto',
                        '&:hover': {
                            bgcolor: alpha('#fff', 0.1),
                        },
                    }}
                >
                    <Close sx={{ pointerEvents: 'none' }} />
                </IconButton>

                {/* T4lo Robot Image with Progress */}
                <Box sx={{ mb: 0, position: 'relative' }}>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <Box
                            component="img"
                            src="/T4lo_Robot.png"
                            alt="T4lo Robot"
                            sx={{
                                width: { xs: 120, md: 180 },
                                height: { xs: 120, md: 180 },
                                mx: 'auto',
                                mb: 1,
                                display: 'block',
                                animation: isInProgress ? 'pulse 2s infinite' : 'none',
                            }}
                        />

                        {/* Progress Ring
                        {isInProgress && (
                            <CircularProgress
                                variant="determinate"
                                value={progressPercent}
                                size={window.innerWidth >= 900 ? 188 : 128}
                                thickness={3}
                                sx={{
                                    position: 'absolute',
                                    top: -4,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    color: alpha('#fff', 0.8),
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round',
                                    },
                                }}
                            />
                        )} */}
                    </Box>
                </Box>

                {/* Welcome Message with Progress */}
                <Box sx={{ mb: 0 }}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 500,
                            background: `linear-gradient(45deg, #fff 30%, ${alpha('#fff', 0.8)} 90%)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: `0 2px 4px ${alpha('#000', 0.3)}`,
                            mb: 1,
                        }}
                    >
                        {isCompleted
                            ? `Willkommen zurück, ${firstName}!`
                            : isInProgress
                              ? `Willkommen zurück, ${firstName}!`
                              : `Willkommen bei T4lo, ${firstName}!`}
                    </Typography>

                    {/* Progress Badge */}
                    {isInProgress && (
                        <Chip
                            label={`${progressPercent}% abgeschlossen`}
                            size="small"
                            sx={{
                                bgcolor: alpha('#fff', 0.2),
                                color: 'white',
                                fontWeight: 600,
                                border: `1px solid ${alpha('#fff', 0.3)}`,
                            }}
                        />
                    )}
                </Box>

                {/* Subtitle for progress */}
                {isInProgress && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: alpha('#fff', 0.7),
                            mb: 1,
                            fontWeight: 500,
                        }}
                    >
                        Noch {totalSteps - currentStep} Schritte bis zum Abschluss
                    </Typography>
                )}

                <Typography
                    variant="body2"
                    sx={{
                        mb: isInProgress ? 3 : 4,
                        color: alpha('#fff', 0.9),
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                    }}
                >
                    {isCompleted
                        ? 'Ihr Onboarding ist abgeschlossen. Sie können jederzeit Ihr Profil erweitern oder neue Skills hinzufügen.'
                        : isInProgress
                          ? `Ihr Onboarding ist zu ${progressPercent}% abgeschlossen. Lassen Sie uns dort weitermachen, wo Sie aufgehört haben.`
                          : 'Ihr persönliches Onboarding steht bereit. Lassen Sie uns gemeinsam Ihr Talentprofil erstellen und Ihre Fähigkeiten optimal präsentieren.'}
                </Typography>

                {/* Progress Bar for in-progress */}
                {isInProgress && (
                    <Box sx={{ mb: 3 }}>
                        <LinearProgress
                            variant="determinate"
                            value={progressPercent}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: alpha('#fff', 0.2),
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    backgroundColor: alpha('#fff', 0.8),
                                },
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                textAlign: 'center',
                                mt: 1,
                                color: alpha('#fff', 0.7),
                                fontWeight: 500,
                            }}
                        >
                            Schritt {currentStep + 1} von {totalSteps}
                        </Typography>
                    </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {isCompleted ? (
                        <>
                            <Button
                                variant="outlined"
                                onClick={handleClose}
                                sx={{
                                    color: 'white !important',
                                    borderColor: alpha('#fff', 0.3),
                                    '&:hover': {
                                        borderColor: 'white',
                                        bgcolor: alpha('#000', 0.2),
                                        color: 'white !important',
                                    },
                                }}
                            >
                                Später
                            </Button>
                            <SpaceGradientButton
                                onClick={handleContinue}
                                endIcon={<ArrowForward />}
                                sx={{
                                    background: `linear-gradient(45deg, ${alpha('#fff', 0.2)} 0%, ${alpha('#fff', 0.1)} 100%)`,
                                    color: 'white',
                                    border: `1px solid ${alpha('#fff', 0.3)}`,
                                    '&:hover': {
                                        background: `linear-gradient(45deg, ${alpha('#fff', 0.3)} 0%, ${alpha('#fff', 0.2)} 100%)`,
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                Profil erweitern
                            </SpaceGradientButton>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                onClick={handleClose}
                                sx={{
                                    color: 'white !important',
                                    borderColor: alpha('#fff', 0.3),
                                    '&:hover': {
                                        borderColor: 'white',
                                        bgcolor: alpha('#000', 0.2),
                                        color: 'white !important',
                                    },
                                }}
                            >
                                Später
                            </Button>
                            <SpaceGradientButton
                                onClick={isInProgress ? handleContinue : handleStart}
                                endIcon={isInProgress ? <TrendingUp /> : <ArrowForward />}
                                sx={{
                                    background: `linear-gradient(45deg, ${alpha('#fff', 0.2)} 0%, ${alpha('#fff', 0.1)} 100%)`,
                                    color: 'white',
                                    border: `1px solid ${alpha('#fff', 0.3)}`,
                                    '&:hover': {
                                        background: `linear-gradient(45deg, ${alpha('#fff', 0.3)} 0%, ${alpha('#fff', 0.2)} 100%)`,
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                {isInProgress ? 'Onboarding fortsetzen' : 'Onboarding starten'}
                            </SpaceGradientButton>
                        </>
                    )}
                </Box>

                {/* Decorative Elements */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -20,
                        left: -20,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha('#fff', 0.2)} 0%, transparent 70%)`,
                        animation: 'pulse 2s infinite',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -15,
                        right: -15,
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha('#fff', 0.15)} 0%, transparent 70%)`,
                        animation: 'pulse 2s infinite 0.5s',
                    }}
                />
            </Box>

            <style>
                {`
          @keyframes pulse {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.1); }
          }
        `}
            </style>
        </Box>
    );
}
